import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ProfilePage from './pages/ProfilePage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminStaff from './pages/AdminStaff';
import AdminClients from './pages/AdminClients';
import AdminCases from './pages/AdminCases';
import AdminVisaPrograms from './pages/AdminVisaPrograms';
import B2BPartners from './pages/B2BPartners';
import ClientPortal from './pages/ClientPortal';
import ClientComplaints from './pages/ClientComplaints';
import AdminChecklistTemplates from './pages/AdminChecklistTemplates';
import CaseDetail from './pages/CaseDetail';
import UsersManagement from './pages/UsersManagement';
import SettingsPage from './pages/SettingsPage';
import EmailLogs from './pages/EmailLogs';
import EmailTemplates from './pages/EmailTemplates';
import OfficerDocuments from './pages/OfficerDocuments';
import CaseOfficerDashboard from './pages/CaseOfficerDashboard';
import OfficerComplaints from './pages/OfficerComplaints';
import DocumentLibrary from './pages/DocumentLibrary';
import EmailModule from './pages/EmailModule';
import AccountingDashboard from './pages/AccountingDashboard';
import SalesManagement from './pages/SalesManagement';
import ExpenseManagement from './pages/ExpenseManagement';
import FinancialReports from './pages/FinancialReports';
import ComplaintNaturesManagement from './pages/ComplaintNaturesManagement';
import NotificationsPage from './pages/NotificationsPage';
import AdminClientDetail from './pages/AdminClientDetail';
import SalesDashboard from './pages/sales/SalesDashboard';
import SalesManagerDashboard from './pages/sales/SalesManagerDashboard';
import LeadsKanban from './pages/sales/LeadsKanban';
import LeadDetail from './pages/sales/LeadDetail';
import MarketingLeadsUpload from './pages/marketing/MarketingLeadsUpload';
import MarketingImportHistory from './pages/marketing/MarketingImportHistory';
import LeadPool from './pages/marketing/LeadPool';
import MarketingDashboard from './pages/marketing/MarketingDashboard';
import LeadSources from './pages/marketing/LeadSources';
import SalesReports from './pages/sales/SalesReports';
import LegalDashboard from './pages/legal/LegalDashboard';
import LegalEscalationDetail from './pages/legal/LegalEscalationDetail';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="app-container">
          <ErrorBoundary>
          <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Profile Routes (all authenticated users) */}
            <Route path="/profile" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'case_officer', 'client', 'accountant', 'sales_consultant', 'sales_manager', 'marketing_manager']}>
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="/change-password" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'case_officer', 'client', 'accountant', 'sales_consultant', 'sales_manager', 'marketing_manager']}>
                <ChangePasswordPage />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes (Super Admin & Process Manager) */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/staff" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <AdminStaff />
              </ProtectedRoute>
            } />
            <Route path="/admin/clients" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <AdminClients />
              </ProtectedRoute>
            } />
            <Route path="/admin/cases" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <AdminCases />
              </ProtectedRoute>
            } />
            <Route path="/admin/visa-programs" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <AdminVisaPrograms />
              </ProtectedRoute>
            } />
            <Route path="/admin/b2b-partners" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <B2BPartners />
              </ProtectedRoute>
            } />
            <Route path="/admin/checklists" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <AdminChecklistTemplates />
              </ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <UsersManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <SettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/email-logs" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <EmailLogs />
              </ProtectedRoute>
            } />
            <Route path="/admin/email-templates" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <EmailTemplates />
              </ProtectedRoute>
            } />
            <Route path="/admin/document-library" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <DocumentLibrary />
              </ProtectedRoute>
            } />
            <Route path="/admin/email" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <EmailModule />
              </ProtectedRoute>
            } />
            <Route path="/admin/complaint-types" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <ComplaintNaturesManagement />
              </ProtectedRoute>
            } />
            
            {/* Accounting Routes (Super Admin, Admin & Accountant) */}
            <Route path="/admin/accounting" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'accountant']}>
                <AccountingDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/accounting/sales" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'accountant']}>
                <SalesManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/accounting/expenses" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'accountant']}>
                <ExpenseManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/accounting/reports" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'accountant']}>
                <FinancialReports />
              </ProtectedRoute>
            } />
            
            {/* Accountant Routes */}
            <Route path="/accountant" element={
              <ProtectedRoute allowedRoles={['accountant']}>
                <AccountingDashboard />
              </ProtectedRoute>
            } />
            <Route path="/accountant/accounting" element={
              <ProtectedRoute allowedRoles={['accountant']}>
                <AccountingDashboard />
              </ProtectedRoute>
            } />
            <Route path="/accountant/accounting/sales" element={
              <ProtectedRoute allowedRoles={['accountant']}>
                <SalesManagement />
              </ProtectedRoute>
            } />
            <Route path="/accountant/accounting/expenses" element={
              <ProtectedRoute allowedRoles={['accountant']}>
                <ExpenseManagement />
              </ProtectedRoute>
            } />
            <Route path="/accountant/accounting/reports" element={
              <ProtectedRoute allowedRoles={['accountant']}>
                <FinancialReports />
              </ProtectedRoute>
            } />
            <Route path="/accountant/email" element={
              <ProtectedRoute allowedRoles={['accountant']}>
                <EmailModule />
              </ProtectedRoute>
            } />
            
            {/* Process Manager Routes */}
            <Route path="/manager" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/manager/cases" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <AdminCases />
              </ProtectedRoute>
            } />
            <Route path="/manager/clients" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <AdminClients />
              </ProtectedRoute>
            } />
            <Route path="/manager/officers" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <AdminStaff />
              </ProtectedRoute>
            } />
            <Route path="/manager/performance" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/manager/users" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <UsersManagement />
              </ProtectedRoute>
            } />
            <Route path="/manager/settings" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <SettingsPage />
              </ProtectedRoute>
            } />
            
            {/* Case Officer Routes */}
            <Route path="/officer" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <CaseOfficerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/officer/cases" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <AdminCases />
              </ProtectedRoute>
            } />
            <Route path="/officer/cases/:caseId" element={
              <ProtectedRoute allowedRoles={['case_officer', 'super_admin', 'admin', 'process_manager']}>
                <CaseDetail />
              </ProtectedRoute>
            } />
            <Route path="/officer/documents" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <OfficerDocuments />
              </ProtectedRoute>
            } />
            <Route path="/officer/document-library" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <DocumentLibrary />
              </ProtectedRoute>
            } />
            <Route path="/officer/email" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <EmailModule />
              </ProtectedRoute>
            } />
            <Route path="/officer/complaints" element={
              <ProtectedRoute allowedRoles={['case_officer']}>
                <OfficerComplaints />
              </ProtectedRoute>
            } />
            
            {/* Admin Client Detail Route */}
            <Route path="/admin/clients/:clientId" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <AdminClientDetail />
              </ProtectedRoute>
            } />

            {/* Admin Case Detail Route */}
            <Route path="/admin/cases/:caseId" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager']}>
                <CaseDetail />
              </ProtectedRoute>
            } />
            
            {/* Manager Case Detail Route */}
            <Route path="/manager/cases/:caseId" element={
              <ProtectedRoute allowedRoles={['admin', 'process_manager']}>
                <CaseDetail />
              </ProtectedRoute>
            } />
            
            {/* Client Routes */}
            <Route path="/client" element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientPortal />
              </ProtectedRoute>
            } />
            <Route path="/client/application" element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientPortal />
              </ProtectedRoute>
            } />
            <Route path="/client/documents" element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientPortal />
              </ProtectedRoute>
            } />
            <Route path="/client/complaints" element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientComplaints />
              </ProtectedRoute>
            } />
            
            {/* ── Sales Consultant Routes ── */}
            <Route path="/sales" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager', 'super_admin', 'admin', 'process_manager']}>
                <SalesDashboard />
              </ProtectedRoute>
            } />
            <Route path="/sales/leads" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager', 'super_admin', 'admin', 'process_manager']}>
                <LeadsKanban />
              </ProtectedRoute>
            } />
            <Route path="/sales/leads/:leadId" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager', 'super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <LeadDetail />
              </ProtectedRoute>
            } />
            <Route path="/sales/email" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager']}>
                <EmailModule />
              </ProtectedRoute>
            } />
            <Route path="/sales/attendance" element={
              <ProtectedRoute allowedRoles={['sales_consultant', 'sales_manager', 'super_admin', 'admin', 'process_manager']}>
                <SalesDashboard />
              </ProtectedRoute>
            } />

            {/* ── Sales Reports ── */}
            <Route path="/sales/reports" element={
              <ProtectedRoute allowedRoles={['sales_manager', 'super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <SalesReports />
              </ProtectedRoute>
            } />

            {/* ── Sales Manager Routes ── */}
            <Route path="/sales/manager" element={
              <ProtectedRoute allowedRoles={['sales_manager', 'super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <SalesManagerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/sales/manager/leads" element={
              <ProtectedRoute allowedRoles={['sales_manager', 'super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <LeadsKanban />
              </ProtectedRoute>
            } />
            <Route path="/sales/manager/leaderboard" element={
              <ProtectedRoute allowedRoles={['sales_manager', 'super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <SalesManagerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/sales/manager/attendance" element={
              <ProtectedRoute allowedRoles={['sales_manager', 'super_admin', 'admin', 'process_manager']}>
                <SalesManagerDashboard />
              </ProtectedRoute>
            } />

            {/* ── Marketing Module Routes ── */}
            <Route path="/admin/marketing" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <MarketingDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/upload" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <MarketingLeadsUpload />
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/history" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <MarketingImportHistory />
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/pool" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager', 'sales_manager']}>
                <LeadPool />
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/analytics" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'marketing_manager']}>
                <MarketingDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing/sources" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'marketing_manager']}>
                <LeadSources />
              </ProtectedRoute>
            } />

            {/* Legal Counsel Routes */}
            <Route path="/legal" element={
              <ProtectedRoute allowedRoles={['legal_counsel', 'super_admin', 'admin', 'process_manager']}>
                <LegalDashboard />
              </ProtectedRoute>
            } />
            <Route path="/legal/escalations" element={
              <ProtectedRoute allowedRoles={['legal_counsel', 'super_admin', 'admin', 'process_manager']}>
                <LegalDashboard />
              </ProtectedRoute>
            } />
            <Route path="/legal/escalations/:id" element={
              <ProtectedRoute allowedRoles={['legal_counsel', 'super_admin', 'admin', 'process_manager', 'case_officer', 'sales_consultant', 'client']}>
                <LegalEscalationDetail />
              </ProtectedRoute>
            } />
            <Route path="/legal/analytics" element={
              <ProtectedRoute allowedRoles={['legal_counsel', 'super_admin', 'admin', 'process_manager']}>
                <LegalDashboard />
              </ProtectedRoute>
            } />

            {/* Notifications — add new roles */}
            <Route path="/notifications" element={
              <ProtectedRoute allowedRoles={['super_admin', 'admin', 'process_manager', 'case_officer', 'client', 'accountant', 'sales_consultant', 'sales_manager', 'marketing_manager', 'legal_counsel']}>
                <NotificationsPage />
              </ProtectedRoute>
            } />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          </ErrorBoundary>
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
